import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Signature } from '@shared/data/entity/fields/SignatureField';
import { useSelector } from '@shared/store';
import { API } from '@shared/types/api';
import { FileUpload } from '@shared/types/fieldValues';
import { containedImageUrl, fullSizeImageUri, greyscaleImageUri } from '@shared/util/media/image';
import { getExtensionFromUrl } from '@shared/util/media/type';
import { getController } from '@shared/util/timeout';

interface Props {
  image: FileUpload | API.ChatAttachmentFragment | Signature;
  containerWidth?: number;
  containerHeight?: number;
  size?: 'full' | 'contain';
  grayscale?: boolean;
  heightOffset?: number;
  widthOffset?: number;
}

interface ImageSrcAndDimensions {
  src: string;
  width: number;
  height: number;
  ratio: number;
  isLandscape: boolean;
  isPortrait: boolean;
  /** True once the browser has confirmed the image's natural dimensions. */
  resolved: boolean;
}

/**
 * Images reach us with no usable density metadata. Imgix strips it while
 * re-encoding, so the bytes the browser decodes no longer say whether 900
 * pixels were meant to be 900 points or 450. We therefore guess the source
 * density from the file type.
 *
 * PNGs are mostly retina screenshots, so they are assumed 2x and lay out at
 * the size they were captured at. GIFs are mostly memes and JPEGs mostly web
 * images or photos, so they are assumed 1x. A large phone photo hits the
 * container bound either way. Unknown types keep the 2x default.
 *
 * The content type isn't always set to the real image type, so the file
 * extension is the fallback, as it is when chat decides what is an image.
 */
const DEFAULT_SOURCE_DPR = 2;

const SOURCE_DPR_BY_IMAGE_TYPE: Record<string, number> = {
  gif: 1,
  jpeg: 1,
  jpg: 1,
  webp: 1,
};

function assumedSourceDpr({ contentType, name, url }: Props['image']): number {
  const candidates = [
    contentType?.toLowerCase().replace(/^image\//, ''),
    getExtensionFromUrl(name ?? ''),
    getExtensionFromUrl(url ?? ''),
  ];
  const known = candidates.find(type => !isNil(type) && type in SOURCE_DPR_BY_IMAGE_TYPE);
  return isNil(known) ? DEFAULT_SOURCE_DPR : SOURCE_DPR_BY_IMAGE_TYPE[known];
}

/**
 * Given an image object and its container width/height,
 * returns an Imgix src and the width and height of the image.
 *
 * Will return the fullsize image src or an src for the max image size
 * that will fit in the given container dimensions.
 *
 * Display size assumes a source density from the file type, so a retina
 * screenshot lays out at the size it was captured at rather than at twice it,
 * and a 1x GIF or JPEG lays out at its own pixel size.
 *
 * Returns ratio, isLandscape, and isPortrait convenience values.
 */
export function useImageSrcAndDimensions({
  image,
  containerWidth,
  containerHeight,
  size = 'full',
  grayscale = false,
  widthOffset = 0,
  heightOffset = 0,
}: Props): ImageSrcAndDimensions {
  const viewportWidth = useSelector(state => state.appState.viewportWidth);
  const viewportHeight = useSelector(state => state.appState.viewportHeight);
  const [naturalDimensions, setNaturalDimensions] = useState<[number, number]>();
  const [resolved, setResolved] = useState(false);

  const colorUrl = grayscale ? greyscaleImageUri(image.url) : image.url;
  const src =
    size === 'full'
      ? fullSizeImageUri(colorUrl)
      : containedImageUrl(colorUrl, [containerWidth ?? 0, containerHeight ?? 0]);

  // Load the image in the browser to get post-EXIF naturalWidth/naturalHeight.
  useEffect(() => {
    const { signal, abort } = getController();
    const img = new window.Image();

    img.addEventListener(
      'load',
      () => {
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;
        if (nw > 0 && nh > 0) {
          setNaturalDimensions([nw, nh]);
        }
        setResolved(true);
      },
      { signal },
    );

    setResolved(false);
    img.src = src;

    return abort;
  }, [src]);

  // Layout comes from the source image's own pixel dimensions, never from the
  // file at `src`: in "contain" mode imgix has already resized that file to the
  // container times the viewer's screen dpr, so measuring it would fold the
  // viewer's screen density into the layout.
  //
  // The browser's post-EXIF natural dimensions still decide which way round the
  // image is, because the server can report raw sensor dimensions that ignore
  // the rotation the browser applies (common with iOS camera uploads). They are
  // also the fallback when the server reports no dimensions at all.
  const intrinsicDimensions = useMemo<[number | undefined, number | undefined]>(() => {
    const reported: [number | undefined, number | undefined] = [
      image.width ?? undefined,
      image.height ?? undefined,
    ];
    if (isNil(naturalDimensions)) {
      return reported;
    }
    if (isNil(reported[0]) || isNil(reported[1])) {
      return naturalDimensions;
    }
    const reportedIsLandscape = reported[0] >= reported[1];
    const naturalIsLandscape = naturalDimensions[0] >= naturalDimensions[1];
    return reportedIsLandscape === naturalIsLandscape ? reported : [reported[1], reported[0]];
  }, [image.width, image.height, naturalDimensions]);

  const ratio = (intrinsicDimensions[0] ?? 1) / (intrinsicDimensions[1] ?? 1);
  const isLandscape = ratio >= 1;
  const isPortrait = ratio < 1;

  const sourceDpr = assumedSourceDpr(image);

  // Compute display dimensions synchronously so the container never starts at
  // the 56×56 minimum before jumping to its real size. This prevents the layout
  // shift that causes the scroll view to over-adjust when images first appear.
  const dimensions = useMemo(() => {
    const [w, h] = intrinsicDimensions;
    if (isNil(w) || isNil(h)) {
      return [undefined, undefined];
    }
    const maxWidth = !isNil(containerWidth) ? containerWidth : viewportWidth;
    const maxHeight = !isNil(containerHeight) ? containerHeight : viewportHeight;
    if (isNil(maxWidth) || isNil(maxHeight)) {
      return [undefined, undefined];
    }
    // The size the image asks to be drawn at, before it has to fit anywhere.
    const naturalW = w / sourceDpr;
    const naturalH = h / sourceDpr;
    // "contain" scaling: use the most restrictive scale so image fits both dimensions
    const scale = Math.min(maxWidth / naturalW, maxHeight / naturalH, 1);
    return [naturalW * scale + widthOffset, naturalH * scale + heightOffset];
  }, [
    intrinsicDimensions,
    sourceDpr,
    containerWidth,
    containerHeight,
    widthOffset,
    heightOffset,
    viewportWidth,
    viewportHeight,
  ]);

  return {
    src,
    width: dimensions[0] ?? 0,
    height: dimensions[1] ?? 0,
    ratio,
    isLandscape,
    isPortrait,
    resolved,
  };
}
